import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faTimes, faUserPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { xorBy } from "lodash";

import type { AttendeeAttrs } from "~/common/Attendees";
import recaptcha from "~/lib/recaptcha";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import type {
	MeetingInviteUsersMutation,
	MeetingInviteUsersMutation$data,
} from "~/__generated__/MeetingInviteUsersMutation.graphql";

import styles from "./css/GenericInvite.module.css";

const InviteMeetingUsersMutation = graphql`
	mutation MeetingInviteUsersMutation($input: InviteMeetingUsersMutationInput!) {
		inviteMeetingUsers(input: $input) {
			invitees {
				errors {
					field
					message
				}
				user {
					id
					email
					image
					fullName
					isActive
					isConfigured
					isGuest
				}
			}
			event {
				id
				attendees {
					invitable
					email
					fullName
					image
					user {
						id
						isActive
						isConfigured
						isGuest
					}
				}
			}
		}
	}
`;

type Attendee = AttendeeAttrs & { email: string };
export type MeetingInvitees = NonNullable<MeetingInviteUsersMutation$data["inviteMeetingUsers"]>["invitees"];

interface Props {
	user: Attendee | null;
	attendees: readonly Attendee[];
	eventId: string | undefined;
	noteTitle?: string;
	analytics: { [key: string]: string };
	onClose: (data?: { invitees: MeetingInvitees; inviteSent: boolean }) => void;
}

export default function GenericInvite({ user, attendees, eventId, noteTitle, onClose, analytics }: Props) {
	const [selected, setSelected] = React.useState<Attendee[]>([]);
	const [inviting, setInviting] = React.useState(false);

	/* eslint-disable camelcase */
	const analyticsGenericInvite = React.useMemo(
		() => ({
			tracking_category: "ux__web",
			invite_type: "meeting",
			...analytics,
		}),
		[analytics],
	);
	/* eslint-enable camelcase */

	const closeModal = React.useCallback(
		(data?: { invitees: MeetingInvitees; inviteSent: boolean }) => {
			dispatchAnalyticsTrack("[Invite Dialog] Closed", analyticsGenericInvite);
			onClose(data);
		},
		[onClose, analyticsGenericInvite],
	);

	const cancel = React.useCallback(() => closeModal(), [closeModal]);

	React.useEffect(() => {
		window.hj?.("trigger", "invitedialog_opened");
		dispatchAnalyticsTrack("[Invite Dialog] Opened", analyticsGenericInvite);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		user ? setSelected([user]) : setSelected(attendees.filter(attendee => attendee.invitable));
	}, [user, attendees]);

	const displayError = React.useCallback((msg: string, { reset = false, autoClose = 5000 } = {}) => {
		toast.error(
			<div>
				<strong>Error:</strong> Something went wrong inviting your teammates.
				<br />
				<strong>{msg}</strong>
			</div>,
			{ autoClose },
		);
		if (reset) setSelected([]);
	}, []);

	const inviteAttendees = React.useCallback(async () => {
		const captchaToken = await recaptcha("meeting_invite");

		setInviting(true);

		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[Invite Dialog] Invited", { num_invites: selected.length, ...analyticsGenericInvite });

		if (!eventId) return;

		commitMutation<MeetingInviteUsersMutation>(environment, {
			mutation: InviteMeetingUsersMutation,
			variables: {
				input: {
					captchaToken,
					eventId: eventId,
					analytics: JSON.stringify(analyticsGenericInvite),
					invitations: selected.map((attendee: Attendee) => ({
						email: attendee.email,
						fullName: attendee.fullName || attendee.email,
						sendInviteEmail: true,
					})),
				},
			},
			onCompleted: ({ inviteMeetingUsers }, err) => {
				setInviting(false);

				if (err) return displayError(err[0].message);
				if (inviteMeetingUsers) {
					const { invitees } = inviteMeetingUsers;
					invitees.forEach(({ errors }) => errors?.[0] && displayError(errors[0].message, { autoClose: undefined }));

					const successful = invitees.filter(({ errors }) => !errors?.length);
					if (successful.length) closeModal({ invitees: successful, inviteSent: true });
				}
			},
		});
	}, [selected, analyticsGenericInvite, eventId, displayError, closeModal]);

	const onLabelClick = React.useCallback(
		(attendee: Attendee) => {
			return () => setSelected(xorBy(selected, [attendee], "email"));
		},
		[selected],
	);

	function buildLabel(attendee: Attendee) {
		return (
			<div key={attendee.id} className={styles.inputLabel} data-test-id="meeting-invite-attendee">
				{attendee.fullName}
				<div className={styles.removeLabel}>
					<FontAwesomeIcon icon={faTimes} onClick={onLabelClick(attendee)} />
				</div>
			</div>
		);
	}

	return (
		<Modal onClose={cancel}>
			<Modal.Header onClose={cancel}>
				<Modal.Title>Add your teammates</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.inviteContainer} data-test-id="invite-container">
				<div className={styles.giveAccess}>
					Give <strong>your teammates</strong> access to Fellow to collaborate on{" "}
					<strong>{noteTitle ? noteTitle : "your next meeting together"}</strong>
				</div>

				<div className={styles.contactSearch}>
					<div className={styles.attendees}>{selected.map(buildLabel)}</div>
				</div>

				<div className={styles.invitationWarning}>
					<FontAwesomeIcon icon={faUserPlus} />
					<p>New teammates will be invited to join Fellow.</p>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button
					size="normal"
					variant="primary"
					disabled={!selected.length || inviting}
					onClick={inviteAttendees}
					data-test-id="meetings-invite-send"
				>
					{inviting ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : "Give access"}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
